import { PageBreadcrumbsWithLinks } from '@/components'
import { KpiStatCard } from '@/components/dashboard'
import { dashboardShellBreadcrumb } from '@/constants/adminPage'
import { MenuLinks } from '@/constants/menu'
import { PagingVariables } from '@/constants/paging'
import { messageHelper } from '@/helpers/message.helper'
import { AnimationSkeleton } from '@/pages/ui/Skeleton'
import { PageBody, PageTitle, PageWrapper } from '@/components/PageWrapper'
import { transactionAdminApi } from '@/services/transactionAdminApi'
import {
	SubscriptionPaymentStatusTypes,
	normalizePaymentStatus,
	type AdminTransactionListResponse,
	type TransactionAnalyticsSummaryResponse,
} from '@/services/transactionTypes'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const statusLabel = (status: SubscriptionPaymentStatusTypes | string | number) => {
	switch (normalizePaymentStatus(status)) {
		case SubscriptionPaymentStatusTypes.Succeeded:
			return 'Succeeded'
		case SubscriptionPaymentStatusTypes.Failed:
			return 'Failed'
		case SubscriptionPaymentStatusTypes.Pending:
			return 'Pending'
		case SubscriptionPaymentStatusTypes.Refunded:
			return 'Refunded'
		default:
			return 'Unknown'
	}
}

const statusClass = (status: SubscriptionPaymentStatusTypes | string | number) => {
	switch (normalizePaymentStatus(status)) {
		case SubscriptionPaymentStatusTypes.Succeeded:
			return 'orbit-pill orbit-pill--success'
		case SubscriptionPaymentStatusTypes.Failed:
			return 'orbit-pill orbit-pill--danger'
		case SubscriptionPaymentStatusTypes.Pending:
			return 'orbit-pill orbit-pill--warning'
		default:
			return 'orbit-pill'
	}
}

const formatMoney = (amount: number, currency: string) => {
	try {
		return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(amount)
	} catch {
		return `${amount.toFixed(2)} ${currency.toUpperCase()}`
	}
}

const formatDate = (value: string) => {
	const d = new Date(value)
	return Number.isNaN(d.getTime()) ? value : d.toLocaleString()
}

const ManageTransactions = () => {
	const { t } = useTranslation()
	const [loading, setLoading] = useState(true)
	const [rows, setRows] = useState<AdminTransactionListResponse[]>([])
	const [totalCount, setTotalCount] = useState(0)
	const [pageNumber, setPageNumber] = useState(1)
	const [analytics, setAnalytics] = useState<TransactionAnalyticsSummaryResponse | null>(null)

	const [filterStatus, setFilterStatus] = useState<SubscriptionPaymentStatusTypes | ''>('')
	const [dateFrom, setDateFrom] = useState('')
	const [dateTo, setDateTo] = useState('')
	const [keyword, setKeyword] = useState('')

	const [appliedFilters, setAppliedFilters] = useState({
		status: '' as SubscriptionPaymentStatusTypes | '',
		dateFrom: '',
		dateTo: '',
		keyword: '',
	})

	const filterRequest = useMemo(
		() => ({
			pageNumber,
			pageSize: PagingVariables.DefaultPageSize,
			sortField: 'createdOn',
			sortOrder: 'desc',
			paymentStatus: appliedFilters.status === '' ? undefined : appliedFilters.status,
			dateFrom: appliedFilters.dateFrom ? new Date(appliedFilters.dateFrom).toISOString() : undefined,
			dateTo: appliedFilters.dateTo ? new Date(appliedFilters.dateTo).toISOString() : undefined,
			keyword: appliedFilters.keyword.trim() || undefined,
		}),
		[pageNumber, appliedFilters]
	)

	const load = useCallback(async () => {
		setLoading(true)
		try {
			const [list, summary] = await Promise.all([
				transactionAdminApi.search(filterRequest),
				transactionAdminApi.getAnalyticsSummary({
					...filterRequest,
					pageNumber: 1,
					pageSize: 1,
				}),
			])
			setRows(list.data ?? [])
			setTotalCount(list.totalCount ?? 0)
			setAnalytics(summary)
		} catch (e) {
			messageHelper.showError(String(e))
		} finally {
			setLoading(false)
		}
	}, [filterRequest])

	useEffect(() => {
		load()
	}, [load])

	const onApplyFilters = () => {
		setAppliedFilters({
			status: filterStatus,
			dateFrom,
			dateTo,
			keyword,
		})
		setPageNumber(1)
	}

	const onClearFilters = () => {
		setFilterStatus('')
		setDateFrom('')
		setDateTo('')
		setKeyword('')
		setAppliedFilters({ status: '', dateFrom: '', dateTo: '', keyword: '' })
		setPageNumber(1)
	}

	const totalPages = Math.max(1, Math.ceil(totalCount / PagingVariables.DefaultPageSize))

	return (
		<>
			<PageBreadcrumbsWithLinks
				title={t('Manage.Transactions.Heading', 'Transactions')}
				subNames={dashboardShellBreadcrumb(t('Manage.Transactions.Breadcrumb', 'Transactions'))}
			/>

			<PageWrapper>
				<PageTitle title={t('Manage.Transactions.Heading', 'Transactions')} />

				<PageBody>
					{analytics && (
						<div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
							<KpiStatCard
								label="Total"
								value={analytics.totalCount.toLocaleString()}
								iconClass="ri-exchange-line"
								accentClass="text-primary"
							/>
							<KpiStatCard
								label="Succeeded"
								value={analytics.succeededCount.toLocaleString()}
								iconClass="ri-checkbox-circle-line"
								accentClass="text-success"
								className="[&_p.font-display]:text-emerald-500"
							/>
							<KpiStatCard
								label="Failed"
								value={analytics.failedCount.toLocaleString()}
								iconClass="ri-close-circle-line"
								accentClass="text-destructive"
								className="[&_p.font-display]:text-red-500"
							/>
							<KpiStatCard
								label="Pending"
								value={analytics.pendingCount.toLocaleString()}
								iconClass="ri-time-line"
								accentClass="text-warning"
								className="[&_p.font-display]:text-amber-500"
							/>
							<KpiStatCard
								label="Revenue (succeeded)"
								value={formatMoney(analytics.succeededAmount, analytics.currency)}
								iconClass="ri-money-dollar-circle-line"
								accentClass="text-[#1ed27d]"
							/>
						</div>
					)}

					<div className="card p-4 mb-4">
						<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 items-end">
							<div>
								<label className="form-label text-xs">Status</label>
								<select
									className="form-select w-full"
									value={filterStatus === '' ? '' : String(filterStatus)}
									onChange={(e) =>
										setFilterStatus(e.target.value === '' ? '' : (Number(e.target.value) as SubscriptionPaymentStatusTypes))
									}
								>
									<option value="">All statuses</option>
									<option value={SubscriptionPaymentStatusTypes.Succeeded}>Succeeded</option>
									<option value={SubscriptionPaymentStatusTypes.Failed}>Failed</option>
									<option value={SubscriptionPaymentStatusTypes.Pending}>Pending</option>
									<option value={SubscriptionPaymentStatusTypes.Refunded}>Refunded</option>
								</select>
							</div>
							<div>
								<label className="form-label text-xs">From date</label>
								<input type="date" className="form-input w-full" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
							</div>
							<div>
								<label className="form-label text-xs">To date</label>
								<input type="date" className="form-input w-full" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
							</div>
							<div className="xl:col-span-2">
								<label className="form-label text-xs">Search</label>
								<input
									type="text"
									className="form-input w-full"
									placeholder="Tenant, email, plan, Stripe payment id…"
									value={keyword}
									onChange={(e) => setKeyword(e.target.value)}
									onKeyDown={(e) => e.key === 'Enter' && onApplyFilters()}
								/>
							</div>
						</div>
						<div className="flex flex-wrap gap-2 mt-3">
							<button type="button" className="btn btn-primary btn-sm" onClick={onApplyFilters}>
								Apply filters
							</button>
							<button type="button" className="btn btn-secondary btn-sm" onClick={onClearFilters}>
								Clear
							</button>
						</div>
					</div>

					{loading ? (
						<AnimationSkeleton />
					) : (
						<div className="card overflow-x-auto">
							<table className="w-full text-sm">
								<thead>
									<tr className="border-b border-[hsl(var(--border))] text-left text-[hsl(var(--muted-foreground))]">
										<th className="p-3">Date</th>
										<th className="p-3">Tenant</th>
										<th className="p-3">Plan</th>
										<th className="p-3">Amount</th>
										<th className="p-3">Status</th>
										<th className="p-3">Stripe ID</th>
										<th className="p-3">Invoice</th>
									</tr>
								</thead>
								<tbody>
									{rows.length === 0 ? (
										<tr>
											<td colSpan={7} className="p-6 text-center text-[hsl(var(--muted-foreground))]">
												No transactions found for the selected filters.
											</td>
										</tr>
									) : (
										rows.map((row) => (
											<tr key={row.id} className="border-b border-[hsl(var(--border))]/60 hover:bg-[hsl(var(--muted))]/10">
												<td className="p-3 whitespace-nowrap">{formatDate(row.createdOn)}</td>
												<td className="p-3">
													{row.tenantId ? (
														<Link
															to={MenuLinks.CustomerDetail.replace(':tenantId', String(row.tenantId))}
															className="font-medium text-[hsl(var(--primary))] hover:underline"
														>
															{row.tenantName}
														</Link>
													) : (
														<div className="font-medium">{row.tenantName}</div>
													)}
													<div className="text-xs text-[hsl(var(--muted-foreground))]">{row.tenantAdminEmail}</div>
												</td>
												<td className="p-3">{row.planName}</td>
												<td className="p-3 font-medium">{formatMoney(row.amount, row.currency)}</td>
												<td className="p-3">
													<span className={statusClass(row.paymentStatus)}>{statusLabel(row.paymentStatus)}</span>
												</td>
												<td className="p-3 font-mono text-xs">{row.stripePaymentIntentId ?? '—'}</td>
												<td className="p-3">
													{row.invoiceUrl ? (
														<a href={row.invoiceUrl} target="_blank" rel="noreferrer" className="text-[hsl(var(--primary))] hover:underline">
															View
														</a>
													) : (
														'—'
													)}
												</td>
											</tr>
										))
									)}
								</tbody>
							</table>

							{totalCount > PagingVariables.DefaultPageSize && (
								<div className="flex items-center justify-between p-3 border-t border-[hsl(var(--border))]">
									<p className="text-xs text-[hsl(var(--muted-foreground))]">
										Page {pageNumber} of {totalPages} · {totalCount} total
									</p>
									<div className="flex gap-2">
										<button
											type="button"
											className="btn btn-sm btn-secondary"
											disabled={pageNumber <= 1}
											onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
										>
											Previous
										</button>
										<button
											type="button"
											className="btn btn-sm btn-secondary"
											disabled={pageNumber >= totalPages}
											onClick={() => setPageNumber((p) => p + 1)}
										>
											Next
										</button>
									</div>
								</div>
							)}
						</div>
					)}
				</PageBody>
			</PageWrapper>
		</>
	)
}

export default ManageTransactions
